// packages/web/src/App.tsx
import React, { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { theme } from './styles/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FirebaseService, PlayerSlotService } from '@matchpoint/shared';
import { ScrollToTop } from './components/Common/ScrollToTop';

// Pages
import MainPage from './pages/MainPage';
import LandingPage from './pages/LandingPage';
import PlayerProfilePage from './pages/PlayerProfilePage';
import PlayerSchedulePage from './pages/PlayerSchedulePage';
import CourtsPage from './pages/CourtsPage';
import CourtDetailPage from './pages/CourtDetailPage';
import SubscriptionPlansPage from './pages/SubscriptionPlansPage'
import { CookieBanner } from './components/Layout/CookieBanner';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Import email-related components
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { EmailVerificationPage } from './pages/EmailVerificationPage';
import { EmailPreferencesSettings } from './components/Common/EmailPreferencesSettings';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

// Component to protect routes
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// Inner App component that uses auth
const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  // Initialize other services when App mounts
  useEffect(() => {
    const initializeServices = async () => {
      try {
        const playerSlotService = PlayerSlotService.getInstance();
        await playerSlotService['firebaseService'].initializeApp();
        console.log('Additional services initialized');
      } catch (error) {
        console.error('Failed to initialize additional services:', error);
      }
    };
    
    initializeServices();
  }, []);

  // Don't render routes until auth is initialized
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <Routes>
        {/* Landing page for non-authenticated users, Calendar for authenticated users */}
        <Route path="/" element={user ? <MainPage /> : <LandingPage />} />
        
        {/* Calendar route - always shows MainPage (player slots) */}
        <Route path="/calendar" element={<MainPage />} />
        
        {/* Public routes */}
        <Route path="/players/:userId/schedule" element={<PlayerSchedulePage />} />
        
        {/* Courts routes */}
        <Route path="/courts" element={<CourtsPage />} />
        <Route path="/courts/:courtId" element={<CourtDetailPage />} />
        
        {/* Plans route */}
        <Route path="/plans" element={<SubscriptionPlansPage />} />

        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* Protected routes */}
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <PlayerProfilePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/challenges" 
          element={
            <ProtectedRoute>
              <PlayerProfilePage />
            </ProtectedRoute>
          } 
        />
        {/* Email-related routes */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/activate" element={<EmailVerificationPage />} />
        
        {/* Settings routes */}
        <Route 
          path="/settings/email" 
          element={
            <ProtectedRoute>
              <EmailPreferencesSettings />
            </ProtectedRoute>
          } 
        />

       <Route path="/login" element={user ? <MainPage /> : <LandingPage />} />

        {/* Footer Routes */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/contacts" element={<ContactPage />} />

      </Routes>
      <CookieBanner />
    </Router>
  );
};

// Main App component
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;